import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import { inject, observer } from 'mobx-react';
import { observable } from 'mobx';
import { AnchorButton, Button, Icon, Intent } from '@blueprintjs/core';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Lockr from 'lockr';
import { CurrentEnv } from '../../services/Api';
import Toast from 'helpers/toaster';
import BookingsSidebar from './bookings-sidebar';

// Share booking form: the ways customers can reach the public booking form,
// each with when to use it, a sketch of how it looks and a copy button.
// Replaces the "More Actions" copy-link dialogs. Styles: styles/booking-share.scss.

const SKETCHES = {
  // A browser window showing the form.
  link: (
    <div className="bk-share__sketch-window">
      <span className="bk-share__sketch-bar" />
      <span className="bk-share__sketch-form">
        <i /><i /><i className="is-short" /><b />
      </span>
    </div>
  ),
  // The same, with your logo and contact strip on top.
  brand: (
    <div className="bk-share__sketch-window">
      <span className="bk-share__sketch-bar" />
      <span className="bk-share__sketch-brand"><em /><i /></span>
      <span className="bk-share__sketch-form">
        <i /><i className="is-short" /><b />
      </span>
    </div>
  ),
  // Your website with the form placed inside it.
  embed: (
    <div className="bk-share__sketch-window">
      <span className="bk-share__sketch-bar" />
      <span className="bk-share__sketch-site"><i /><i className="is-short" /></span>
      <span className="bk-share__sketch-form is-framed">
        <i /><i className="is-short" /><b />
      </span>
    </div>
  ),
  // Your website with the step-by-step form inside it.
  steps: (
    <div className="bk-share__sketch-window">
      <span className="bk-share__sketch-bar" />
      <span className="bk-share__sketch-site"><i className="is-short" /></span>
      <span className="bk-share__sketch-form is-framed">
        <span className="bk-share__sketch-steps"><s className="is-on" /><s className="is-on" /><s /><s /></span>
        <i /><b />
      </span>
    </div>
  ),
};

const iframeCode = (src) =>
  `<iframe src="${src}" style="width: 100%; height: 2000px; border: 0;" title="Book a service"></iframe>`;

@inject('viewStore') @observer
class BookingShare extends Component {

  @observable copied = null;

  get options() {
    const tenant = Lockr.get('currentTenant');
    if (!tenant) return [];
    const base = `${CurrentEnv.appUrl}/booking_public/${tenant.id}/bookings`;
    return [
      {
        id: 'link',
        title: 'Booking page link',
        when: 'Send it by email or text, or put it on social media. Customers open the booking form on its own page.',
        kind: 'link',
        value: `${base}/new`,
        preview: `${base}/new`,
      },
      {
        id: 'brand',
        title: 'Branded booking page link',
        when: 'The same page, with your logo, email and phone number across the top so customers know it\'s you.',
        kind: 'link',
        value: `${base}/new?is_brand=true`,
        preview: `${base}/new?is_brand=true`,
      },
      {
        id: 'embed',
        title: 'Embed the full form on your website',
        when: 'Paste this code into a page on your website. The whole booking form appears inside it.',
        kind: 'code',
        value: iframeCode(`${base}/new`),
        preview: `${base}/new`,
      },
      {
        id: 'steps',
        title: 'Embed the step-by-step form',
        when: 'A compact form that asks one thing at a time. Good for a sidebar or a small section of a page.',
        kind: 'code',
        value: iframeCode(`${base}/booking-modal`),
        preview: `${base}/booking-modal`,
      },
    ];
  }

  onCopy(option) {
    this.copied = option.id;
    Toast.show({
      icon: 'tick',
      message: option.kind === 'code' ? 'Embed code copied' : 'Link copied',
      intent: Intent.SUCCESS,
    });
    clearTimeout(this.copiedTimer);
    this.copiedTimer = setTimeout(() => { this.copied = null; }, 2000);
  }

  componentWillUnmount() {
    clearTimeout(this.copiedTimer);
  }

  renderOption(option) {
    const copied = this.copied === option.id;
    const fieldId = `bk-share-${option.id}`;
    return (
      <article key={option.id} className={`bk-share__option bk-share__option--${option.id}`} aria-labelledby={`${fieldId}-title`}>
        <div className="bk-share__sketch" aria-hidden="true">{SKETCHES[option.id]}</div>
        <div className="bk-share__content">
          <h2 id={`${fieldId}-title`} className="bk-share__title">{option.title}</h2>
          <p className="bk-share__when">{option.when}</p>
          <label htmlFor={fieldId} className="bk-share__sr">
            {option.kind === 'code' ? `Embed code for ${option.title}` : option.title}
          </label>
          {option.kind === 'code' ? (
            <textarea
              id={fieldId}
              className="bk-share__value bk-share__value--code"
              readOnly={true}
              rows={4}
              value={option.value}
              onFocus={(event) => event.target.select()}
            />
          ) : (
            <input
              id={fieldId}
              className="bk-share__value"
              readOnly={true}
              value={option.value}
              onFocus={(event) => event.target.select()}
            />
          )}
          <div className="bk-share__actions">
            <CopyToClipboard text={option.value} onCopy={() => this.onCopy(option)}>
              <Button intent={Intent.PRIMARY} icon={copied ? 'tick' : 'duplicate'} className="bk-share__copy">
                {copied ? 'Copied' : option.kind === 'code' ? 'Copy code' : 'Copy link'}
              </Button>
            </CopyToClipboard>
            <AnchorButton
              minimal={true}
              icon="share"
              href={option.preview}
              target="_blank"
              rel="noopener noreferrer"
            >
              {option.kind === 'code' ? 'Preview form' : 'Open page'}
            </AnchorButton>
          </div>
        </div>
      </article>
    );
  }

  render() {
    const options = this.options;
    return (
      <div>
        <Helmet>
          <title>Share booking form | FieldNexus</title>
        </Helmet>
        <div className="row no-gutters">
          <div className="col-lg-2 sidebar-responsive">
            <BookingsSidebar />
          </div>
          <div className="col-lg-10">
            <div className="bk-share">
              <header className="bk-share__header">
                <div>
                  <h1 className="bk-share__heading">Share your booking form</h1>
                  <p className="bk-share__intro">
                    Customers use it to request a service. New requests show up in Booking requests.
                  </p>
                </div>
                <a className="bk-share__settings" href="/dashboard/bookings/booking-configuration">
                  <Icon icon="cog" iconSize={14} />
                  Change how the form looks
                </a>
              </header>

              {options.length === 0 ? (
                <div className="bk-share__missing" role="alert">
                  <Icon icon="warning-sign" iconSize={16} />
                  Your company details didn't load, so the links can't be built. Sign out and back in, then try again.
                </div>
              ) : (
                <div className="bk-share__grid">
                  {options.map((option) => this.renderOption(option))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default BookingShare;
